import { Phone, Menu, X, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import anemtLogo from "@/assets/anemt-logo-new.png";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services & Pricing", path: "/pricing" },
    { name: "About Us", path: "/about" },
    { name: "FAQ", path: "/faq" },
    { name: "Request a Ride", path: "/contact#booking-form" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-card backdrop-blur-sm border-b border-border shadow-sm safe-top">
      <div className="container mx-auto px-4">
        <div className="flex h-16 sm:h-20 items-center justify-between">
          {/* Mobile menu button - left side */}
          <button
            className="lg:hidden p-2.5 -ml-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Logo - centered on mobile, left on desktop */}
          <Link to="/" className="flex items-center gap-3 lg:mr-8">
            <img src={anemtLogo} alt="ANEMT Logo" width="64" height="64" className="h-16 sm:h-20 w-auto object-contain mix-blend-multiply" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-5 xl:space-x-8 flex-1 justify-center">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  "text-base font-medium transition-colors hover:text-primary whitespace-nowrap py-1",
                  isActive(link.path) ? "text-primary" : "text-foreground"
                )}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center">
            <Button asChild size="sm" className="bg-button text-button-foreground hover:bg-accent/90 rounded-xl shadow-sm">
              <a href="tel:9713445446" className="flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                971-344-5446
              </a>
            </Button>
          </div>

          {/* Mobile CTA - right side */}
          <a
            href="tel:9713445446"
            className="lg:hidden p-2.5 -mr-2 rounded-lg hover:bg-muted transition-colors text-accent"
            aria-label="Call 971-344-5446"
          >
            <Phone className="h-5 w-5" />
          </a>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 top-[calc(4rem)] sm:top-[calc(5rem)] bg-foreground/20 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav className="lg:hidden absolute top-full left-0 right-0 bg-card border-b border-border shadow-lg z-50 animate-slide-down safe-bottom">
            <div className="container mx-auto px-4 py-3">
              <div className="flex flex-col space-y-1">
                {navLinks.map(link => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "text-base font-medium py-3 px-4 rounded-xl transition-colors",
                      isActive(link.path)
                        ? "text-primary bg-primary/5"
                        : "text-foreground hover:bg-muted"
                    )}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
              <div className="flex flex-col gap-2.5 mt-4 pt-4 border-t border-border pb-2">
                <Button asChild size="lg" className="w-full text-base h-12 bg-button text-button-foreground rounded-xl">
                  <a href="tel:9713445446" className="flex items-center justify-center">
                    <Phone className="h-5 w-5 mr-2" />
                    Call 971-344-5446
                  </a>
                </Button>
                <Button asChild size="lg" className="w-full text-base h-12 bg-button text-button-foreground rounded-xl">
                  <Link to="/contact#booking-form" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-center">
                    <Calendar className="h-5 w-5 mr-2" />
                    Request a Ride
                  </Link>
                </Button>
              </div>
            </div>
          </nav>
        </>
      )}
    </header>
  );
};

export default Header;
